import type { MouseEvent } from 'react';
import type { QuestionChoice, Size } from '@/types';
import type { DropdownProps } from '@/components/dropdown/dropdown.types';
import { useState, useRef, useMemo, useEffect } from 'react';
import { useBoolean, useOnClickOutside, useResizeObserver } from 'usehooks-ts';
import InputControl from '@/components/input-control';
import IconChevron from '@/icons/chevron';
import IconMagnifyingGlass from '@/icons/magnifying-glass';
import { clickHasNode } from '@/utils/helper';
import '@/components/dropdown/dropdown.css';
import Converter from '@/utils/converter';

export default function Dropdown(props: DropdownProps) {
  const {
    choices = [],
    value = [],
    placeholder = 'Wählen Sie Ihre private Krankenversicherung...',
    noResult = 'Keine Versicherung gefunden.',
    isSearchHidden = false,
    multiple = false,
    closeButton = 'Schließen',
    onChange,
    onOpen,
    onClose,
  } = props;
  const borderRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { value: isOpen, setFalse: closeDropdown, toggle: toggleDropdown } = useBoolean(false);
  const [search, setSearch] = useState<string>('');
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });
  const [selectedChoices, setSelectedChoices] = useState<QuestionChoice[]>([]);

  useResizeObserver({
    ref: boxRef,
    onResize: (e) => setSize({ width: e.width || 0, height: e.height || 0 }),
  });

  useOnClickOutside(boxRef, (e) => {
    if (clickHasNode(e, borderRef)) return;
    if (multiple) onChange(selectedChoices);
    closeDropdown();
  });

  useEffect(() => setSelectedChoices(Array.isArray(value) ? value : []), [value]);

  useEffect(() => {
    if (isOpen && size.width && size.height) {
      inputRef.current?.focus();
      onOpen?.call(null, size.height, size.width);
    } else {
      onClose?.call(null);
    }
  }, [isOpen, size]);

  const handleSelection = (newValue: string, newLabel: string) => {
    setSelectedChoices((prev) => {
      const exists = prev.some(({ value }) => value === newValue);
      return exists ? prev.filter(({ value }) => value !== newValue) : [...prev, { value: newValue, label: newLabel }];
    });
  };

  const onCloseButtonClick = (e: MouseEvent): void => {
    e.preventDefault();
    closeDropdown();
    onChange(selectedChoices);
  };

  const visibleChoices = useMemo(
    () => choices.filter(({ label }) => label.toLowerCase().includes(search.toLowerCase())),
    [search, choices],
  );

  return (
    <div className='dropdown'>
      <div ref={borderRef} className='dropdown__border' onClick={toggleDropdown}>
        <span className='dropdown__value'>
          {selectedChoices.length > 0
            ? Converter.Answer.FromArrayToMultiple(selectedChoices.map((selectedChoice) => selectedChoice.label))
            : placeholder}
        </span>
        <div className='dropdown__chevron-wrap'>
          <IconChevron data-dropdown-open={isOpen} className='dropdown__chevron' />
        </div>
      </div>
      <div ref={boxRef} data-visible={isOpen} className='dropdown__box'>
        <div data-visible={!isSearchHidden} className='dropdown__search'>
          <input
            ref={inputRef}
            value={search}
            type='text'
            className='dropdown__input'
            onChange={(e) => setSearch(e.target.value)}
          />
          <IconMagnifyingGlass className='dropdown__glass' />
        </div>
        <ul className='dropdown__choices'>
          {visibleChoices.length ? (
            visibleChoices.map(({ value, label }, i) => (
              <li
                key={value + i}
                className='dropdown__choice'
                onClick={() => {
                  if (multiple) {
                    handleSelection(value, label);
                  } else {
                    closeDropdown();
                    onChange([{ value, label }]);
                  }
                }}
              >
                {multiple && (
                  <InputControl
                    shape='checkbox'
                    checked={selectedChoices.some((selectedChoice) => selectedChoice.value === value)}
                    className='dropdown__input-control'
                  />
                )}
                <span data-with-checkbox={multiple}>{label}</span>
              </li>
            ))
          ) : (
            <li className='dropdown__choice dropdown__choice--no-result'>{noResult}</li>
          )}
        </ul>
        {multiple && (
          <div className='dropdown__close'>
            <button className='dropdown__close-button' onClick={onCloseButtonClick}>
              {closeButton}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
